import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ReactComponent as DashboardIcon } from "assets/icons/dashboard.svg";
import { ReactComponent as UserIcon } from "assets/icons/user.svg";
import { ReactComponent as PawIcon } from "assets/icons/paw.svg";
import { ReactComponent as CalendarIcon } from "assets/icons/calendar.svg";
import { ReactComponent as SettingsIcon } from "assets/icons/settings.svg";
import { ReactComponent as UserGroupIcon } from "assets/icons/user-group.svg";
import { ReactComponent as StethoscopeIcon } from "assets/icons/stethoscope.svg";
import { ReactComponent as ChevronIcon } from "assets/icons/chevron.svg";
import patientFallbackImage from "assets/images/patient-fallback.svg";
// import { ReactComponent as LogoutIcon } from "assets/icons/logout.svg";

const panelItems = [
  {
    id: "dashboard",
    label: "Dashboard",
    path: "dashboard",
    icon: <DashboardIcon />,
  },
  {
    id: "clients",
    label: "Clients",
    path: "clients",
    icon: <UserIcon />,
  },
  {
    id: "patients",
    label: "Patients",
    path: "patients",
    icon: <PawIcon />,
  },
  {
    id: "calendar",
    label: "Clinic Calendar",
    path: "calendar",
    icon: <CalendarIcon />,
  },
  {
    id: "services",
    label: "Services",
    path: "services",
    icon: <SettingsIcon />,
  },
  {
    id: "staff",
    label: "Staff",
    path: "staff",
    icon: <UserGroupIcon />,
  },
  {
    id: "clinics",
    label: "Clinics",
    path: "clinics",
    icon: <StethoscopeIcon />,
  },
  {
    id: "profile",
    label: "Profile",
    path: "profile",
    icon: <UserIcon />,
  },
];

type Props = {
  /** Drawer state. Only meaningful below lg, where the panel is an overlay. */
  open: boolean;
  onClose: () => void;
  /** Rail mode. Only meaningful from lg up - the drawer is always full width. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
};

const LeftPannel = ({ open, onClose, collapsed, onToggleCollapsed }: Props) => {
  const { pathname } = useLocation();

  // Close the drawer whenever navigation happens, otherwise tapping a link on a
  // phone leaves the overlay covering the page you just navigated to.
  React.useEffect(() => {
    onClose();
  }, [pathname, onClose]);

  // Escape closes it, which is the expected behaviour for an overlay and costs
  // nothing to support.
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  return (
    <>
      {/* Scrim, below lg only. Static sidebar never needs one. */}
      {open && (
        <div
          className="fixed inset-0 z-20 bg-slate-900/40 lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}

      <div
        className={
          "z-30 flex w-60 shrink-0 flex-col border-r border-slate-200 bg-white " +
          "fixed inset-y-0 left-0 transition-all duration-200 ease-out " +
          "lg:static lg:translate-x-0 " +
          (open ? "translate-x-0 " : "-translate-x-full ") +
          // Collapsed applies from lg only. On a phone the drawer is always full
          // width - a rail you have to open anyway would be worse than useless.
          (collapsed ? "lg:w-[4.5rem]" : "lg:w-60")
        }
      >
      <div
        className={
          "h-16 shrink-0 flex items-center border-b border-slate-200 " +
          (collapsed ? "lg:justify-center lg:px-0 pl-5 pr-6" : "pl-5 pr-6")
        }
      >
        <img
          src={patientFallbackImage}
          alt=""
          className={
            "h-8 w-8 flex-shrink-0 rounded-full " +
            (collapsed ? "mr-2.5 lg:mr-0" : "mr-2.5")
          }
        />
        <span
          className={
            "text-lg font-bold tracking-tight text-vg-blue " +
            (collapsed ? "lg:hidden" : "")
          }
        >
          VETGEO
        </span>
      </div>
      <nav className="py-3 flex flex-col gap-0.5">
        {panelItems.map((item) => {
          const to = `/app/${item.path}`;
          const isNestedSection =
            item.id === "clients" ||
            item.id === "patients" ||
            item.id === "services" ||
            item.id === "staff" ||
            item.id === "clinics";

          return (
            <NavLink
              key={item.id}
              to={to}
              end={!isNestedSection && item.id !== "dashboard"}
              className={({ isActive }) => {
                const active =
                  item.id === "dashboard"
                    ? pathname === "/app" ||
                      pathname === "/app/dashboard" ||
                      pathname.startsWith("/app/dashboard/")
                    : isActive;
                return (
                  "flex items-center gap-x-3 px-3 py-2 mx-2 rounded-md text-sm font-medium transition-colors duration-150 " +
                  (collapsed ? "lg:justify-center lg:px-0 lg:mx-2 " : "") +
                  (active
                    ? "text-vg-blue bg-vg-blue/[0.08]"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900")
                );
              }}
              // The label is the only accessible name once it is visually hidden,
              // so keep it as the title for hover and screen readers.
              title={collapsed ? item.label : undefined}
            >
              {item.icon}
              <span className={collapsed ? "lg:hidden" : ""}>{item.label}</span>
            </NavLink>
          );
        })}
        </nav>

        {/* Collapse toggle, lg and up. Below lg the drawer is opened from the
            header instead, so a rail control would have nothing to do. */}
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          aria-pressed={collapsed}
          className={
            "mt-auto hidden shrink-0 items-center gap-x-3 border-t border-slate-200 px-5 py-3 " +
            "text-sm font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900 " +
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-vg-blue/30 lg:flex " +
            (collapsed ? "lg:justify-center lg:px-0" : "")
          }
        >
          <ChevronIcon
            className={
              "h-4 w-4 shrink-0 transition-transform duration-200 " +
              (collapsed ? "rotate-90" : "-rotate-90")
            }
          />
          <span className={collapsed ? "lg:hidden" : ""}>Collapse</span>
        </button>
      </div>
    </>
  );
};

export default LeftPannel;
